/**
 * Cancelling this account's own orders at one rung — the half of a ladder
 * click that needs no gate.
 *
 * Shared by the pro ladder (a click on your own size) and the standard book
 * (the ✕ on your order's mark). It lives apart from `useLadderTrading` because
 * the standard book offers it with click to trade switched **off**, where the
 * placing half — a 4 Hz gate over the balance, the positions and the clock — is
 * never mounted. A cancel carries no signature and no money, only the session
 * cookie, so there is nothing for a gate to protect, and every reason the gate
 * closes (no size, a stale book, `CANCEL_ONLY`, an expired key) is a reason the
 * user most needs to pull what is resting.
 */

import type { Runtime } from '@/app/runtime'
import type { StoreSet } from '@/stores'
import { pushToast } from '@/stores/ui'
import type { Outcome } from '@/lib/decimal'
import { ownBookFrom, upRungOf, type LadderSide } from '@/domain/pro-ladder'

/** What a cancel click did. `none` means there was nothing of the account's here. */
export type CancelOutcome = 'cancelled' | 'pending' | 'none'

/**
 * Cancel every order of this account's at one rung of the UP-normalised book.
 *
 * `shownCents` is the price as the user sees it — the DOWN book shows the same
 * rung at `100 − p` — and is only ever used in the sentence.
 *
 * **Pending-only is refused, and that is a safety rule.** A submitted order has
 * no `order_id` until its receipt lands, and a marketable one legitimately sits
 * pending for the whole `taker_delay_ms` (D12). On the pro ladder a click that
 * found nothing to cancel would fall through to placing, so without this a
 * click meant to undo an order would double it.
 */
export async function cancelOwnAt(
  runtime: Runtime,
  marketId: string,
  upSide: LadderSide,
  upCents: number,
  cell: HTMLElement,
  shownCents: number = upCents,
): Promise<CancelOutcome> {
  const own = ownAt(runtime.stores, marketId, upSide, upCents)
  if (own.orderIds.length > 0) {
    cell.dataset.busy = '1'
    try {
      // `trading.cancel` toasts its own failures and treats an order that has
      // already gone (`ORDER_NOT_CANCELLABLE`, `ORDER_NOT_FOUND`) as done.
      await Promise.all(own.orderIds.map((id) => runtime.trading.cancel(id)))
    } finally {
      delete cell.dataset.busy
    }
    return 'cancelled'
  }
  if (own.pendingMicro > 0n) {
    pushToast({
      tone: 'info',
      title: 'Still going through',
      body: `Your ${shownCents}¢ order has not come back yet. It can be cancelled once it has.`,
    })
    return 'pending'
  }
  return 'none'
}

/**
 * Cancel the account's orders on one row of the standard book, which shows
 * either outcome's ladder: the row is mapped onto the UP rung the orders are
 * kept on, and the sentence keeps the price the user saw.
 */
export function cancelOwnOnRow(
  runtime: Runtime,
  marketId: string,
  outcome: Outcome,
  side: LadderSide,
  cents: number,
  row: HTMLElement,
): Promise<CancelOutcome> {
  const rung = upRungOf(outcome, side, cents)
  return cancelOwnAt(runtime, marketId, rung.side, rung.cents, row, cents)
}

/**
 * This account's own size at one rung, read at click time.
 *
 * From the stores rather than from a memo: acting on a render-old set of order
 * ids would cancel an order that has already gone and miss one that has just
 * arrived. It is the **same projection the ladders paint from**, so the mark
 * the user clicked and the orders that get cancelled can never disagree about
 * which rung an order is on — including the `BUY DOWN` an uncovered ask comes
 * back as.
 */
export function ownAt(
  stores: StoreSet | null | undefined,
  marketId: string,
  side: LadderSide,
  priceCents: number,
): { orderIds: string[]; pendingMicro: bigint } {
  if (!stores) return { orderIds: [], pendingMicro: 0n }
  const book = ownBookFrom(
    Array.from(stores.orders.store.state.byId.values()) as Record<string, unknown>[],
    Array.from(stores.pendingOrders.store.state.byId.values()),
    marketId,
  )
  const level = book[side].get(priceCents)
  return {
    orderIds: level?.orderIds ?? [],
    pendingMicro: level?.pendingMicro ?? 0n,
  }
}
